import { PINNED_ATTRIBUTE_KEY } from './const'
import {
  type ExportAttributeDefinition,
  type ExportAttributeGroup,
} from './types'
import { type SchemaAttributes, type SchemaGroups } from '@/Data/typesV2'

export function buildAttributeDefinitions(
  groupsOrder: string[] | undefined,
  schemaGroups: SchemaGroups | undefined,
  schemaAttributes: SchemaAttributes,
  otherGroupTitle: string,
): ExportAttributeDefinition[] {
  const groupedKeys = new Set<string>()
  const definitions: ExportAttributeDefinition[] = []

  ;(groupsOrder ?? []).forEach((groupKey) => {
    const group = schemaGroups?.[groupKey]
    if (!group) return

    group.attributeOrder.forEach((attributeKey) => {
      if (attributeKey === PINNED_ATTRIBUTE_KEY) return

      const attribute = schemaAttributes[attributeKey]
      if (!attribute) return

      groupedKeys.add(attributeKey)
      definitions.push({
        key: attributeKey,
        label: attribute.title || attributeKey,
        groupKey,
        groupTitle: group.title || groupKey,
      })
    })
  })

  Object.entries(schemaAttributes).forEach(([attributeKey, attribute]) => {
    if (
      attributeKey === PINNED_ATTRIBUTE_KEY ||
      groupedKeys.has(attributeKey)
    ) {
      return
    }

    definitions.push({
      key: attributeKey,
      label: attribute.title || attributeKey,
      groupKey: 'other',
      groupTitle: otherGroupTitle,
    })
  })

  return definitions
}

export function buildFilteredGroups(
  attributes: ExportAttributeDefinition[],
  searchValue: string,
): ExportAttributeGroup[] {
  const normalizedSearch = searchValue.trim().toLowerCase()
  const filteredAttributes = normalizedSearch
    ? attributes.filter((attribute) => {
        return (
          attribute.label.toLowerCase().includes(normalizedSearch) ||
          attribute.key.toLowerCase().includes(normalizedSearch) ||
          attribute.groupTitle.toLowerCase().includes(normalizedSearch)
        )
      })
    : attributes

  const groupMap = new Map<string, ExportAttributeGroup>()

  filteredAttributes.forEach((attribute) => {
    const existingGroup = groupMap.get(attribute.groupKey)

    if (existingGroup) {
      existingGroup.attributes.push(attribute)
      return
    }

    groupMap.set(attribute.groupKey, {
      key: attribute.groupKey,
      title: attribute.groupTitle,
      attributes: [attribute],
    })
  })

  return Array.from(groupMap.values())
}

export function insertKeyInReferenceOrder(
  currentKeys: string[],
  nextKey: string,
  referenceKeys: string[],
) {
  return reorderKeysByReference([...currentKeys, nextKey], referenceKeys)
}

export function reorderKeysByReference(
  currentKeys: string[],
  referenceKeys: string[],
) {
  const selectedKeySet = new Set(currentKeys)

  return referenceKeys.filter((key) => selectedKeySet.has(key))
}

export function areArraysEqual(left: string[], right: string[]) {
  if (left.length !== right.length) return false

  return left.every((value, index) => value === right[index])
}

export function stringifyPreviewValue(value: unknown) {
  if (value === null || value === undefined || value === '') return ''

  return String(value)
}

export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined
}
